import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Users, ShieldCheck, FileText, CalendarDays, CalendarRange, AlertTriangle, PaperclipIcon, UserX, FilePlus2 } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { PageHeader, StatCard, Money, StatusBadge, Spinner, EmptyState } from "@/components/common";
import BillDrawer from "@/components/BillDrawer";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney, riyadhLongDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Trend = ({ data }) => (
  <div className="panel p-5 fade-up" data-testid="dashboard-trend-chart">
    <div className="flex items-baseline justify-between mb-4">
      <div><div className="eyebrow">Entries · last 14 days</div><div className="text-xs text-slate-400 mt-0.5">Daily bills recorded (Asia/Riyadh)</div></div>
    </div>
    <div className="h-56">
      <ResponsiveContainer width="100%" height={224} minWidth={200}>
        <AreaChart data={data.map((d) => ({ ...d, label: fmtDate(d.date).slice(0, 5) }))} margin={{ left: -20, right: 8 }}>
          <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#047857" stopOpacity={0.25} /><stop offset="100%" stopColor="#047857" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} formatter={(v, n) => (n === "vat" ? [`${fmtMoney(v)} SAR`, "VAT"] : [v, "Bills"])} />
          <Area type="monotone" dataKey="count" stroke="#047857" strokeWidth={2} fill="url(#g)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const RankList = ({ title, rows, testid, link }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="panel p-5 fade-up" data-testid={testid}>
      <div className="eyebrow mb-4">{title}</div>
      {!rows.length ? <div className="text-sm text-slate-400">No entries yet.</div> : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id}>
              <Link to={link(r)} className="flex justify-between text-sm hover:text-emerald-700"><span className="truncate pr-2">{r.name}</span><span className="num text-xs text-slate-500">{r.count}</span></Link>
              <div className="h-1.5 bg-slate-100 rounded mt-1.5"><div className="h-1.5 bg-emerald-600 rounded" style={{ width: `${(r.count / max) * 100}%` }} /></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RecentBills = ({ rows, onOpen }) => (
  <div className="panel overflow-hidden fade-up" data-testid="dashboard-recent-bills">
    <div className="px-5 py-4 border-b border-slate-200 flex justify-between items-center"><div className="eyebrow">Recent purchase bills</div><Link to="/bills" className="text-xs text-emerald-700 hover:underline">View all</Link></div>
    {!rows.length ? <EmptyState title="No bills recorded yet" /> : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr>{["Record", "Client", "Vendor / Invoice", "Total", "Status", "Entered"].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((b, i) => (
              <tr key={b.id} className="row-hover cursor-pointer" onClick={() => onOpen(b.id)} data-testid={`recent-bill-row-${i}`}>
                <td className="td num text-xs text-slate-500">{b.record_no}</td>
                <td className="td truncate max-w-[180px]">{b.client_name}</td>
                <td className="td"><div className="truncate max-w-[200px]">{b.vendor_name}</div><div className="num text-[11px] text-slate-400">{b.invoice_number}</div></td>
                <td className="td"><Money value={b.total} strong /></td>
                <td className="td"><StatusBadge status={b.record_status} /></td>
                <td className="td num text-[11px] text-slate-500">{fmtDateTime(b.created_at)}<div>{b.created_by_name}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const Alert = ({ icon: Icon, label, value, to, tone, testid }) => (
  <Link to={to} data-testid={testid} className={`flex items-center gap-3 px-4 py-3 border-b last:border-0 border-slate-100 hover:bg-slate-50 transition-colors`}>
    <Icon className={`w-4 h-4 ${value ? tone : "text-slate-300"}`} />
    <span className="flex-1 text-sm text-slate-700">{label}</span>
    <span className={`num text-sm font-semibold ${value ? tone : "text-slate-400"}`}>{value}</span>
  </Link>
);

const AdminExtras = ({ d }) => (
  <div className="grid lg:grid-cols-3 gap-4">
    <div className="panel overflow-hidden fade-up" data-testid="dashboard-alerts">
      <div className="px-4 py-3 border-b border-slate-200 eyebrow">Attention required</div>
      <Alert icon={UserX} label="Clients without in-charge staff" value={d.unassigned_clients.length} to="/clients?unassigned=1" tone="text-red-600" testid="alert-unassigned" />
      <Alert icon={PaperclipIcon} label="Bills missing attachments" value={d.missing_attachments} to="/bills?missing=1" tone="text-orange-600" testid="alert-missing" />
      <Alert icon={AlertTriangle} label="Unlinked / failed uploads" value={d.orphan_uploads} to="/reports" tone="text-slate-700" testid="alert-orphans" />
      {d.unassigned_clients.length > 0 && (
        <div className="px-4 py-3 bg-red-50/50 text-xs text-red-800 space-y-1">{d.unassigned_clients.slice(0, 4).map((c) => <div key={c.id}>{c.code} · {c.company_name}</div>)}</div>
      )}
    </div>
    <RankList title="Client-wise entries" rows={d.client_wise} testid="dashboard-client-wise" link={(r) => `/bills?client_id=${r.id}`} />
    <RankList title="Staff-wise entries" rows={d.staff_wise} testid="dashboard-staff-wise" link={(r) => `/bills?staff_id=${r.id}`} />
  </div>
);

const Activity = ({ rows }) => (
  <div className="panel fade-up" data-testid="dashboard-recent-activity">
    <div className="px-5 py-4 border-b border-slate-200 flex justify-between"><div className="eyebrow">Recent activity</div><Link to="/audit-logs" className="text-xs text-emerald-700 hover:underline">Audit logs</Link></div>
    <ul className="divide-y divide-slate-100">
      {rows.map((a) => (
        <li key={a.id} className="px-5 py-2.5 text-sm">
          <div className="flex justify-between gap-2"><span className="font-medium text-slate-700">{a.user_name}</span><span className="num text-[11px] text-slate-400">{fmtDateTime(a.at)}</span></div>
          <div className="text-xs text-slate-500 truncate"><span className="num text-emerald-700">{a.action}</span> · {a.details}</div>
        </li>
      ))}
    </ul>
  </div>
);

const MyClients = ({ rows, role }) => {
  const nav = useNavigate();
  return (
    <div className="panel fade-up" data-testid="dashboard-my-clients">
      <div className="px-5 py-4 border-b border-slate-200 eyebrow">{role === "staff" ? "My assigned clients" : "Authorised clients"}</div>
      {!rows.length ? <EmptyState title="No clients assigned yet" subtitle="Ask the Super Admin to assign clients to you." /> : (
        <ul className="divide-y divide-slate-100">
          {rows.slice(0, 8).map((c) => (
            <li key={c.id} className="px-5 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-emerald-50 text-emerald-800 flex items-center justify-center"><Building2 className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{c.company_name}</div><div className="num text-[11px] text-slate-400">{c.code} · {c.vat_number}</div></div>
              {role === "staff" ? <Button size="sm" variant="outline" onClick={() => nav(`/bills/new?client=${c.id}`)} data-testid={`dashboard-add-bill-${c.code}`}><FilePlus2 className="w-3.5 h-3.5 mr-1" />Add purchase</Button>
                : <Button size="sm" variant="outline" onClick={() => nav(`/bills?client_id=${c.id}`)}>View history</Button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <Spinner />;
  const admin = user.role === "super_admin";
  const auditor = user.role === "auditor";
  const kpis = admin
    ? [["Active clients", d.active_clients, Building2], ["Active staff", d.active_staff, Users], ["Auditors", d.auditors, ShieldCheck], ["Bills today", d.bills.today, FileText], ["This week", d.bills.week, CalendarDays], ["This month", d.bills.month, CalendarRange]]
    : auditor
      ? [["Bills today", d.bills.today, FileText], ["This week", d.bills.week, CalendarDays], ["This month", d.bills.month, CalendarRange, "slate"], ["Missing attachments", d.missing_attachments, PaperclipIcon, "amber"]]
      : [["My bills today", d.bills.today, FileText], ["This week", d.bills.week, CalendarDays], ["This month", d.bills.month, CalendarRange, "slate"], ["Missing attachments", d.missing_attachments, PaperclipIcon, "amber"]];
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={riyadhLongDate()}
        title={`Welcome, ${user.name.split(" ")[0]}`}
        subtitle={admin ? "System-wide overview of clients, staff output and bill records." : auditor ? "Bill records of your authorised clients." : "Your assigned clients and recent entries."}
        actions={!auditor && <Button onClick={() => nav("/bills/new")} className="bg-emerald-800 hover:bg-emerald-900" data-testid="dashboard-add-bill-button"><FilePlus2 className="w-4 h-4 mr-1.5" />Purchase bill entry</Button>} />
      <div className={`grid grid-cols-2 md:grid-cols-3 ${admin ? "xl:grid-cols-6" : "xl:grid-cols-4"} gap-4`}>
        {kpis.map(([l, v, I, tone], i) => <StatCard key={l} label={l} value={v} icon={I} tone={tone} delay={i * 50} testid={`kpi-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} />)}
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <Trend data={d.trend} />
        <div className="panel p-5 bg-[#064E3B] text-white border-[#064E3B] fade-up relative overflow-hidden grain" data-testid="dashboard-month-vat">
          <div className="relative z-10">
            <div className="text-[11px] uppercase tracking-[0.14em] text-emerald-300">VAT recorded · this month</div>
            <div className="num text-3xl font-semibold mt-3">{fmtMoney(d.month_vat)}<span className="text-sm text-emerald-300 ml-1.5">SAR</span></div>
            <div className="mt-6 text-[11px] uppercase tracking-[0.14em] text-emerald-300">Gross invoice value</div>
            <div className="num text-xl mt-1">{fmtMoney(d.month_total)} <span className="text-xs text-emerald-300">SAR</span></div>
            <div className="mt-6 text-xs text-emerald-200/80 border-t border-white/10 pt-4">Missing attachments: <span className="num text-amber-300">{d.missing_attachments}</span></div>
          </div>
        </div>
      </div>
      {admin && <AdminExtras d={d} />}
      <div className="grid lg:grid-cols-[1fr_380px] gap-4">
        <RecentBills rows={d.recent_bills} onOpen={setOpen} />
        {admin ? <Activity rows={d.recent_activity} /> : <MyClients rows={d.my_clients} role={user.role} />}
      </div>
      <BillDrawer billId={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} />
    </div>
  );
}
